import { t } from '../../i18n/translations';
import {
    ACTION__MODAL__TAB__BUILDINGS,
    ACTION__MODAL__TAB__BUILDINGS__CRAFT,
    ACTION__MODAL__TAB__BUILDINGS__SUPPORT,
    ACTION__MODAL__TAB__JOBS,
    ACTION__MODAL__TAB__JOBS__CRAFT,
    ACTION__MODAL__TAB__JOBS__SUPPORT,
    ACTION__MODAL__TAB__PRODUCTION,
    ACTION__SCOUT__CONFIRM,
    ACTION__SCOUT__FOOD,
    ACTION__SCOUT__UNIT,
    ACTION__SETTLEMENT__SET_BLACKSMITH_TOOL_TARGET,
    ACTION__SETTLEMENT__SET_BLACKSMITH_WEAPON_TARGET,
    ACTION__SETTLEMENT__BUILD_BARRACKS,
    ACTION__SETTLEMENT__BUILD_BLACKSMITH,
    ACTION__SETTLEMENT__HIRE_MILITIA,
    ACTION__SETTLEMENT__HIRE_SMITH,
    ACTION__TILE__OPEN_SCOUT,
    ACTION__TILE__RETURN_TROOPS,
    ACTION__UI__OPEN_BATTLE_REPORT,
} from '../../engine/input/actionIds';
import type { TutorialContext, TutorialStep } from './types';
import {
    getActiveBuildingsTabId,
    getActiveJobsTabId,
    getActiveModal,
    getEnemyTileId,
    getNearestSettlementId,
} from './helpers';
import type { ConstructionItem } from '../../engine/economy/construction';
import { getBuildingStorage } from '../../engine/settlement/utils';

const MILITIA_TARGET = 4;
const WEAPON_TARGET = 4;
const TOOL_TARGET = 0;

export function buildWeaponsSteps(ctx: TutorialContext): TutorialStep[] {
    const { state, settlement, settlementId, settlementTileId, lang } = ctx;
    const enemyTileId = getEnemyTileId(state, settlementTileId);
    const scoutSettlementId = enemyTileId
        ? getNearestSettlementId(state, enemyTileId, settlementId)
        : settlementId;
    const scoutActionId = scoutSettlementId
        ? `${ACTION__TILE__OPEN_SCOUT}:${scoutSettlementId}`
        : ACTION__TILE__OPEN_SCOUT;
    const missionId = 'scout_1';
    const reportId = 'battle_report_1';
    const reportActionId = reportId
        ? `${ACTION__UI__OPEN_BATTLE_REPORT}:${reportId}`
        : ACTION__UI__OPEN_BATTLE_REPORT;
    const returnActionId = `${ACTION__TILE__RETURN_TROOPS}:${missionId}`;
    const scoutUnitPlus = `${ACTION__SCOUT__UNIT}:militia:plus`;
    const scoutUnitMinus = `${ACTION__SCOUT__UNIT}:militia:minus`;
    const scoutFoodPlus = `${ACTION__SCOUT__FOOD}:plus`;
    const scoutFoodMinus = `${ACTION__SCOUT__FOOD}:minus`;

    return [
        {
            id: 'click-city',
            title: t(lang, 'tutorial.weapons.step1.title'),
            body: t(lang, 'tutorial.weapons.step1.body'),
            target: { type: 'tile', tileId: settlementTileId },
            allowActionIds: [],
            condition: () => state.activeModalId === settlementTileId,
        },
        {
            id: 'open-buildings',
            title: t(lang, 'tutorial.weapons.step2.title'),
            body: t(lang, 'tutorial.weapons.step2.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__BUILDINGS },
            allowActionIds: [ACTION__MODAL__TAB__BUILDINGS],
            condition: () => getActiveModal(state)?.activeTabId === ACTION__MODAL__TAB__BUILDINGS,
        },
        {
            id: 'open-craft-buildings',
            title: t(lang, 'tutorial.weapons.step3.title'),
            body: t(lang, 'tutorial.weapons.step3.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__BUILDINGS__CRAFT },
            allowActionIds: [ACTION__MODAL__TAB__BUILDINGS__CRAFT],
            condition: () =>
                getActiveBuildingsTabId(state) === ACTION__MODAL__TAB__BUILDINGS__CRAFT,
        },
        {
            id: 'build-blacksmith',
            title: t(lang, 'tutorial.weapons.step4.title'),
            body: t(lang, 'tutorial.weapons.step4.body'),
            target: { type: 'action', actionId: ACTION__SETTLEMENT__BUILD_BLACKSMITH },
            allowActionIds: [ACTION__SETTLEMENT__BUILD_BLACKSMITH],
            condition: () =>
                settlement
                    ? settlement.buildings.blacksmith.qty > 0 ||
                      settlement.construction.queue.some(
                          (item: ConstructionItem) => item.building === 'blacksmith',
                      )
                    : false,
        },
        {
            id: 'open-support-buildings',
            title: t(lang, 'tutorial.weapons.step5.title'),
            body: t(lang, 'tutorial.weapons.step5.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__BUILDINGS__SUPPORT },
            allowActionIds: [ACTION__MODAL__TAB__BUILDINGS__SUPPORT],
            condition: () =>
                getActiveBuildingsTabId(state) === ACTION__MODAL__TAB__BUILDINGS__SUPPORT,
        },
        {
            id: 'build-barracks',
            title: t(lang, 'tutorial.weapons.step6.title'),
            body: t(lang, 'tutorial.weapons.step6.body'),
            target: { type: 'action', actionId: ACTION__SETTLEMENT__BUILD_BARRACKS },
            allowActionIds: [ACTION__SETTLEMENT__BUILD_BARRACKS],
            condition: () =>
                settlement
                    ? settlement.buildings.barracks.qty > 0 ||
                      settlement.construction.queue.some(
                          (item: ConstructionItem) => item.building === 'barracks',
                      )
                    : false,
        },
        {
            id: 'open-jobs',
            title: t(lang, 'tutorial.weapons.step7.title'),
            body: t(lang, 'tutorial.weapons.step7.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__JOBS },
            allowActionIds: [ACTION__MODAL__TAB__JOBS],
            condition: () => getActiveModal(state)?.activeTabId === ACTION__MODAL__TAB__JOBS,
        },
        {
            id: 'open-craft-jobs',
            title: t(lang, 'tutorial.weapons.step8.title'),
            body: t(lang, 'tutorial.weapons.step8.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__JOBS__CRAFT },
            allowActionIds: [ACTION__MODAL__TAB__JOBS__CRAFT],
            condition: () => getActiveJobsTabId(state) === ACTION__MODAL__TAB__JOBS__CRAFT,
        },
        {
            id: 'hire-smith',
            title: t(lang, 'tutorial.weapons.step9.title'),
            body: t(lang, 'tutorial.weapons.step9.body'),
            target: { type: 'action', actionId: ACTION__SETTLEMENT__HIRE_SMITH },
            allowActionIds: [ACTION__SETTLEMENT__HIRE_SMITH],
            condition: () => (settlement ? settlement.jobs.smiths >= 1 : false),
        },
        {
            id: 'open-production',
            title: t(lang, 'tutorial.weapons.step10.title'),
            body: t(lang, 'tutorial.weapons.step10.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__PRODUCTION },
            allowActionIds: [ACTION__MODAL__TAB__PRODUCTION],
            condition: () => getActiveModal(state)?.activeTabId === ACTION__MODAL__TAB__PRODUCTION,
        },
        {
            id: 'set-tool-target',
            title: t(lang, 'tutorial.weapons.step11.title'),
            body: t(lang, 'tutorial.weapons.step11.body', TOOL_TARGET),
            target: { type: 'action', actionId: ACTION__SETTLEMENT__SET_BLACKSMITH_TOOL_TARGET },
            allowActionIds: [ACTION__SETTLEMENT__SET_BLACKSMITH_TOOL_TARGET],
            condition: () =>
                settlement ? settlement.production.blacksmith.targetTools === TOOL_TARGET : false,
        },
        {
            id: 'set-weapon-target',
            title: t(lang, 'tutorial.weapons.step12.title'),
            body: t(lang, 'tutorial.weapons.step12.body', WEAPON_TARGET),
            target: { type: 'action', actionId: ACTION__SETTLEMENT__SET_BLACKSMITH_WEAPON_TARGET },
            allowActionIds: [ACTION__SETTLEMENT__SET_BLACKSMITH_WEAPON_TARGET],
            condition: () =>
                settlement
                    ? settlement.production.blacksmith.targetWeapons === WEAPON_TARGET
                    : false,
        },
        {
            id: 'open-jobs-again',
            title: t(lang, 'tutorial.weapons.step13.title'),
            body: t(lang, 'tutorial.weapons.step13.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__JOBS },
            allowActionIds: [ACTION__MODAL__TAB__JOBS],
            condition: () => getActiveModal(state)?.activeTabId === ACTION__MODAL__TAB__JOBS,
        },
        {
            id: 'open-support-jobs',
            title: t(lang, 'tutorial.weapons.step14.title'),
            body: t(lang, 'tutorial.weapons.step14.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__JOBS__SUPPORT },
            allowActionIds: [ACTION__MODAL__TAB__JOBS__SUPPORT],
            condition: () => getActiveJobsTabId(state) === ACTION__MODAL__TAB__JOBS__SUPPORT,
        },
        {
            id: 'hire-militia',
            title: t(lang, 'tutorial.weapons.step15.title'),
            body: t(lang, 'tutorial.weapons.step15.body', MILITIA_TARGET),
            target: { type: 'action', actionId: ACTION__SETTLEMENT__HIRE_MILITIA },
            allowActionIds: [ACTION__SETTLEMENT__HIRE_MILITIA],
            condition: () => (settlement ? settlement.jobs.militia >= MILITIA_TARGET : false),
        },
        {
            id: 'check-weapons',
            title: t(lang, 'tutorial.weapons.step16.title'),
            body: t(lang, 'tutorial.weapons.step16.body'),
            target: { type: 'action', actionId: ACTION__MODAL__TAB__PRODUCTION },
            allowActionIds: [ACTION__MODAL__TAB__PRODUCTION],
            condition: () => {
                if (!settlement) return false;
                if (getActiveModal(state)?.activeTabId !== ACTION__MODAL__TAB__PRODUCTION)
                    return false;
                const hasWeaponWork = settlement.production.blacksmith.task.some(
                    (task, index) =>
                        task === 'weapon' && settlement.production.blacksmith.progress[index] > 0,
                );
                return (
                    hasWeaponWork ||
                    getBuildingStorage(settlement, 'blacksmith').weapons > 0 ||
                    settlement.inventory.weapons >= WEAPON_TARGET
                );
            },
        },
        {
            id: 'click-enemy',
            title: t(lang, 'tutorial.weapons.step17.title'),
            body: t(lang, 'tutorial.weapons.step17.body'),
            target: { type: 'tile', tileId: enemyTileId },
            allowActionIds: [],
            condition: () => state.activeModalId === enemyTileId,
        },
        {
            id: 'open-scout',
            title: t(lang, 'tutorial.weapons.step18.title'),
            body: t(lang, 'tutorial.weapons.step18.body'),
            target: { type: 'action', actionId: scoutActionId },
            allowActionIds: [scoutActionId],
            condition: () =>
                state.modals.some((modal) => modal.id === `scout_modal_${enemyTileId}`),
        },
        {
            id: 'send-scout',
            title: t(lang, 'tutorial.weapons.step19.title'),
            body: t(lang, 'tutorial.weapons.step19.body'),
            target: { type: 'action', actionId: ACTION__SCOUT__CONFIRM },
            allowActionIds: [
                scoutUnitPlus,
                scoutUnitMinus,
                scoutFoodPlus,
                scoutFoodMinus,
                ACTION__SCOUT__CONFIRM,
            ],
            condition: () => state.scoutingMissions.length > 0,
        },
        {
            id: 'open-report',
            title: t(lang, 'tutorial.weapons.step20.title'),
            body: t(lang, 'tutorial.weapons.step20.body'),
            target: { type: 'action', actionId: reportActionId },
            allowActionIds: [reportActionId],
            condition: () => state.modals.some((modal) => modal.id === `battle_report_${reportId}`),
        },
        {
            id: 'return-troops',
            title: t(lang, 'tutorial.weapons.step21.title'),
            body: t(lang, 'tutorial.weapons.step21.body'),
            target: { type: 'action', actionId: returnActionId },
            allowActionIds: [returnActionId],
            condition: () => {
                return state.scoutingMissions.some(
                    (entry) => entry.id === missionId && entry.status === 'returning',
                );
            },
        },
    ];
}
